/**
 * WerkstattAuftragDetailPage — Werkstatt-Auftrag-Detailansicht
 * Route: /werkstatt/auftraege/:id  (neu, Issue #392)
 *        /werkstatt/auftrag/:id    (alt, Issue #394 — Foto-Upload bleibt erreichbar)
 *
 * Issue #392 erweitert die Seite um:
 *   • Stammdaten (Akten-Nr, Kunde, Kennzeichen, aktueller Status)
 *   • Mechaniker-Dropdown (setzt akten.mechaniker_id)
 *   • Feld voraussichtlich_fertig (editierbar)
 *   • Status-Übergang-Buttons (erlaubte Folge-Status)
 *   • Abschnitt „Status-Historie" (aus akte_status_historie, chronologisch)
 *
 * Issue #394 (Foto-Upload) bleibt als letzter Abschnitt erhalten.
 * Issue #393 (Zeiterfassung Start/Stopp pro AW-Position) ebenfalls enthalten.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Calendar, Car, Check, ChevronRight, Clock, Loader2, User, Wrench } from 'lucide-react';
import { Link, useParams } from 'wouter';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import WerkstattAuftragFotoUpload from '@/components/WerkstattAuftragFotoUpload';
import WerkstattZeiterfassung from '@/components/WerkstattZeiterfassung';
import GutachtenCrossLinkPill from '@/components/akte/GutachtenCrossLinkPill';
import WerkstattAuslageSection from '@/components/WerkstattAuslageSection';

// ─── Status constants (mirrors lib/werkstatt-status.ts) ──────────────────────

const WERKSTATT_STATUS = [
  'angenommen', 'in_werkstatt', 'in_arbeit', 'warten_auf_teile',
  'fertig', 'abgeholt', 'rechnung_erstellt', 'bezahlt',
] as const;
type WerkstattStatus = (typeof WERKSTATT_STATUS)[number];

const ALLOWED_TRANSITIONS: Record<string, readonly WerkstattStatus[]> = {
  __initial__: ['angenommen'],
  angenommen: ['in_werkstatt'],
  in_werkstatt: ['in_arbeit'],
  in_arbeit: ['warten_auf_teile', 'fertig'],
  warten_auf_teile: ['in_arbeit', 'fertig'],
  fertig: ['abgeholt'],
  abgeholt: ['rechnung_erstellt'],
  rechnung_erstellt: ['bezahlt'],
  bezahlt: [],
};

function allowedNext(from: WerkstattStatus | null): readonly WerkstattStatus[] {
  const key = from ?? '__initial__';
  return ALLOWED_TRANSITIONS[key] ?? [];
}

const STATUS_LABEL: Record<WerkstattStatus, string> = {
  angenommen: 'Angenommen',
  in_werkstatt: 'In Werkstatt',
  in_arbeit: 'In Arbeit',
  warten_auf_teile: 'Warten auf Teile',
  fertig: 'Fertig',
  abgeholt: 'Abgeholt',
  rechnung_erstellt: 'Rechnung erstellt',
  bezahlt: 'Bezahlt',
};

const STATUS_BADGE: Record<WerkstattStatus, string> = {
  angenommen: 'bg-gray-100 text-gray-700',
  in_werkstatt: 'bg-blue-100 text-blue-700',
  in_arbeit: 'bg-yellow-100 text-yellow-700',
  warten_auf_teile: 'bg-orange-100 text-orange-700',
  fertig: 'bg-green-100 text-green-700',
  abgeholt: 'bg-emerald-100 text-emerald-700',
  rechnung_erstellt: 'bg-purple-100 text-purple-700',
  bezahlt: 'bg-teal-100 text-teal-700',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface WerkstattAuftrag {
  id: string;
  akten_nummer: string | null;
  business_unit: string;
  kunde_name: string | null;
  kennzeichen: string | null;
  werkstatt_status: WerkstattStatus | null;
  mechaniker_id: string | null;
  voraussichtlich_fertig: string | null;
  geloescht: boolean;
  gutachten_akte_id: string | null;
}

interface Mechaniker {
  id: string;
  vorname: string;
  nachname: string;
  kuerzel: string;
  farbe: string | null;
  avatar_url: string | null;
}

interface StatusHistorieEintrag {
  id: string;
  akte_id: string;
  from_status: string | null;
  to_status: string;
  changed_by: string | null;
  changed_at: string;
  notiz: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WerkstattAuftragDetailPage() {
  const params = useParams<{ id: string }>();
  const { user, isAdmin, session } = useAuth();
  const auftragsId = params?.id;

  const [akte, setAkte] = useState<WerkstattAuftrag | null>(null);
  const [mechaniker, setMechaniker] = useState<Mechaniker[]>([]);
  const [historie, setHistorie] = useState<StatusHistorieEintrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingMechaniker, setSavingMechaniker] = useState(false);
  const [savingFertig, setSavingFertig] = useState(false);
  const [fertigInput, setFertigInput] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auftragsId) return;
    setLoading(true);
    try {
      const authHeader: Record<string, string> = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};
      const [akteRes, mechRes, histRes] = await Promise.all([
        fetch(`/api/werkstatt-kanban?action=akte&id=${auftragsId}`, { headers: authHeader }),
        fetch('/api/werkstatt-kanban?action=mechaniker', { headers: authHeader }),
        fetch(`/api/werkstatt-kanban?action=historie&id=${auftragsId}`, { headers: authHeader }),
      ]);
      const [akteJson, mechJson, histJson] = await Promise.all([
        akteRes.json(),
        mechRes.json(),
        histRes.json(),
      ]);
      setAkte(akteJson.akte ?? null);
      setMechaniker(mechJson.mechaniker ?? []);
      setHistorie(histJson.historie ?? []);
      setFertigInput(akteJson.akte?.voraussichtlich_fertig ?? null);
    } catch {
      toast.error('Auftrag konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [auftragsId, session?.access_token]);

  useEffect(() => { load(); }, [load]);

  const nextStatus = useMemo(
    () => allowedNext(akte?.werkstatt_status ?? null),
    [akte?.werkstatt_status],
  );

  async function handleStatusChange(newStatus: WerkstattStatus) {
    if (!akte) return;
    setSavingStatus(true);
    const prevStatus = akte.werkstatt_status;
    setAkte((a) => a ? { ...a, werkstatt_status: newStatus } : a);
    try {
      const res = await fetch('/api/werkstatt-kanban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: 'setStatus',
          akteId: akte.id,
          newStatus,
          userId: user?.id ?? null,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        setAkte((a) => a ? { ...a, werkstatt_status: prevStatus } : a);
        toast.error(json.error ?? 'Status konnte nicht gespeichert werden');
      } else {
        toast.success(`Status: ${STATUS_LABEL[newStatus]}`);
        await load(); // refresh historie
      }
    } catch {
      setAkte((a) => a ? { ...a, werkstatt_status: prevStatus } : a);
      toast.error('Netzwerkfehler');
    } finally {
      setSavingStatus(false);
    }
  }

  async function handleMechanikerChange(mechanikerId: string) {
    if (!akte) return;
    setSavingMechaniker(true);
    const prev = akte.mechaniker_id;
    setAkte((a) => a ? { ...a, mechaniker_id: mechanikerId || null } : a);
    try {
      const res = await fetch('/api/werkstatt-kanban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: 'setMechaniker',
          akteId: akte.id,
          mechanikerId: mechanikerId || null,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        setAkte((a) => a ? { ...a, mechaniker_id: prev } : a);
        toast.error('Mechaniker konnte nicht gespeichert werden');
      }
    } catch {
      setAkte((a) => a ? { ...a, mechaniker_id: prev } : a);
      toast.error('Netzwerkfehler');
    } finally {
      setSavingMechaniker(false);
    }
  }

  async function handleFertigSave() {
    if (!akte) return;
    setSavingFertig(true);
    try {
      const datum = fertigInput ?? null;
      const res = await fetch('/api/werkstatt-kanban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action: 'setVoraussichtlichFertig',
          akteId: akte.id,
          datum,
        }),
      });
      const json = await res.json();
      if (!json.ok) {
        toast.error('Datum konnte nicht gespeichert werden');
      } else {
        setAkte((a) => a ? { ...a, voraussichtlich_fertig: datum } : a);
        toast.success('Fertigstellungsdatum gespeichert');
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setSavingFertig(false);
    }
  }

  if (!auftragsId) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Auftrag nicht gefunden</h2>
        <Link href="/werkstatt/auftraege" className="text-sm text-[#B91C1C] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="auftrag-detail" className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link href="/werkstatt/auftraege" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Aufträge
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
        <span className="font-medium text-gray-900 truncate">
          {akte?.akten_nummer ?? auftragsId}
        </span>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 bg-[#B91C1C] rounded-lg flex items-center justify-center text-white shrink-0">
            <Wrench className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {akte?.akten_nummer ?? '—'}
              </h1>
              {akte?.werkstatt_status && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[akte.werkstatt_status]}`}>
                  {STATUS_LABEL[akte.werkstatt_status]}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
              {akte?.kunde_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> {akte.kunde_name}
                </span>
              )}
              {akte?.kennzeichen && (
                <span className="flex items-center gap-1">
                  <Car className="w-3.5 h-3.5" />
                  <span className="font-mono">{akte.kennzeichen}</span>
                </span>
              )}
            </div>
            {/* Cross-Link zur Gutachten-Akte (Issue #589) */}
            {akte?.gutachten_akte_id && (
              <div className="mt-2">
                <GutachtenCrossLinkPill gutachtenAkteId={akte.gutachten_akte_id} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auslage SV-Büro — Pill + Erstattungs-Sektion (Issue #583) */}
      <WerkstattAuslageSection
        werkstattAkteId={auftragsId}
        accessToken={session?.access_token ?? null}
      />

      {/* Status + Mechaniker + Datum */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Bearbeitung
        </h2>

        {/* Mechaniker */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mechaniker</label>
            <div className="flex items-center gap-2">
              <select
                value={akte?.mechaniker_id ?? ''}
                onChange={(e) => handleMechanikerChange(e.target.value)}
                disabled={savingMechaniker}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C] disabled:opacity-50"
              >
                <option value="">— Kein Mechaniker —</option>
                {mechaniker.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.vorname} {m.nachname}
                  </option>
                ))}
              </select>
              {savingMechaniker && <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />}
            </div>
          </div>

          {/* Voraussichtlich fertig */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Voraussichtlich fertig</label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <DateTimePicker
                  value={fertigInput}
                  onChange={setFertigInput}
                />
              </div>
              <button
                onClick={handleFertigSave}
                disabled={savingFertig}
                className="flex items-center gap-1 px-3 py-2 bg-[#B91C1C] text-white text-xs font-medium rounded-lg hover:bg-[#991B1B] disabled:opacity-50 transition-colors"
              >
                {savingFertig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Next status buttons */}
        {nextStatus.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Status weiterschalten</label>
            <div className="flex flex-wrap gap-2">
              {nextStatus.map((s) => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={savingStatus}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 transition-colors"
                >
                  → {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status-Historie */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Status-Historie
        </h2>
        {historie.length === 0 ? (
          <p className="text-sm text-gray-400">Noch keine Status-Änderungen.</p>
        ) : (
          <ol className="relative border-l border-gray-200 space-y-4 ml-2">
            {historie.map((e) => {
              const mech = e.changed_by
                ? mechaniker.find((m) => m.id === e.changed_by)
                : null;
              return (
                <li key={e.id} className="ml-5">
                  <span className="absolute -left-1.5 mt-0.5 w-3 h-3 rounded-full bg-[#B91C1C] ring-2 ring-white" />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {e.from_status ? STATUS_LABEL[e.from_status as WerkstattStatus] : 'Neu'} →{' '}
                      {STATUS_LABEL[e.to_status as WerkstattStatus] ?? e.to_status}
                    </span>
                    {mech && (
                      <span className="text-xs text-gray-500">
                        {mech.vorname} {mech.nachname}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {new Date(e.changed_at).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {e.notiz && <p className="text-xs text-gray-500 mt-0.5">{e.notiz}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Zeiterfassung (Issue #393) */}
      <WerkstattZeiterfassung
        auftragsId={auftragsId}
        userId={user?.id ?? null}
        accessToken={session?.access_token ?? null}
      />

      {/* Foto-Upload (Issue #394 — bleibt erhalten) */}
      <WerkstattAuftragFotoUpload
        auftragsId={auftragsId}
        userId={user?.id ?? null}
        istAdmin={isAdmin}
      />
    </div>
  );
}
